import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Ticket, Trophy, QrCode, X, CheckCircle, RotateCw } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import PremiumPopup from './PremiumPopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GOLD = '#A9B2B9';

// Coupon options
const COUPON_OPTIONS = [
  { coins: 300, value: '3€', label: '3 Euro Κουπόνι' },
  { coins: 500, value: '5€', label: '5 Euro Κουπόνι' },
  { coins: 1000, value: '10€', label: '10 Euro Κουπόνι' }
];

function RewardShop({ onClose }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('coupons');
  const [coins, setCoins] = useState(0);
  const [myCoupons, setMyCoupons] = useState([]);
  const [mySpins, setMySpins] = useState([]);
  const [contestEntry, setContestEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // For refresh button animation
  const [popup, setPopup] = useState({ show: false, title: '', message: '', type: 'info' });
  const [qrModal, setQrModal] = useState(null);
  const [redeemNotif, setRedeemNotif] = useState(null); // { message }
  const [historyModal, setHistoryModal] = useState(false); // History modal state
  const [history, setHistory] = useState([]); // Coins history
  const sseRef = useRef(null);

  useEffect(() => {
    fetchData();
    connectSSE();

    // Listen for loyalty:refresh events (e.g. after spin wheel)
    const handleRefresh = () => fetchData();
    window.addEventListener('loyalty:refresh', handleRefresh);

    return () => {
      if (sseRef.current) sseRef.current.close();
      window.removeEventListener('loyalty:refresh', handleRefresh);
    };
  }, []);

  // Scroll to top when QR modal opens
  useEffect(() => {
    if (qrModal) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [qrModal]);

  const connectSSE = () => {
    if (sseRef.current) sseRef.current.close();
    const token = localStorage.getItem('session_token') || '';
    const url = token
      ? `${API}/loyalty/stream?session_token=${encodeURIComponent(token)}`
      : `${API}/loyalty/stream`;
    const es = new EventSource(url, { withCredentials: true });
    sseRef.current = es;
    console.log('🔌 SSE Opening...', url.substring(0, 80)); // DEBUG

    es.onopen = () => console.log('✅ SSE Connected!'); // DEBUG
    es.onerror = (err) => console.error('❌ SSE Error:', err); // DEBUG

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        console.log('🔔 SSE Event Received:', data); // DEBUG LOG
        // Update balance live
        if (typeof data.balance === 'number') {
          setCoins(data.balance);
        }
        // Show popup if a redemption notification was sent
        if (data.notification) {
          console.log('🎉 Notification:', data.notification); // DEBUG LOG
          setRedeemNotif({ message: data.notification });
          // Close QR modal if open (coupon was redeemed)
          if (data.notification.includes('εξαργυρώθηκε')) {
            console.log('✅ Closing QR modal in 100ms...'); // DEBUG LOG
            setTimeout(() => {
              setQrModal(null);
              console.log('✅ QR modal closed!'); // DEBUG LOG
            }, 100);
          }
          // Auto-dismiss after 5s
          setTimeout(() => setRedeemNotif(null), 5000);
        }
      } catch (_) {}
    };
    es.onerror = () => { es.close(); };
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setRefreshing(true); // Show refresh animation
      const [coinsRes, couponsRes, contestRes, spinsRes, loyaltyRes] = await Promise.all([
        axios.get(`${API}/coins/balance`, { withCredentials: true }),
        axios.get(`${API}/coupons/my`, { withCredentials: true }),
        axios.get(`${API}/contest/status`, { withCredentials: true }),
        axios.get(`${API}/spins/my`, { withCredentials: true }),
        axios.get(`${API}/loyalty/me`, { withCredentials: true }),
      ]);
      setCoins(coinsRes.data.balance || 0);
      setMyCoupons(couponsRes.data.coupons || []);
      setContestEntry(contestRes.data.entry);
      setMySpins(spinsRes.data.spins || []);
      setHistory(loyaltyRes.data.history || []); // Store full history
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch shop data:', error);
      setLoading(false);
    } finally {
      // Small delay to show animation
      setTimeout(() => setRefreshing(false), 300);
    }
  }, []);

  const handleBuyCoupon = async (option) => {
    if (coins < option.coins) {
      setPopup({
        show: true,
        title: 'Ανεπαρκή Coins',
        message: `Χρειάζεσαι ${option.coins} coins για αυτό το κουπόνι. Έχεις μόνο ${coins} coins.`,
        type: 'error'
      });
      return;
    }

    try {
      await axios.post(`${API}/coupons/buy`, 
        { coins: option.coins, value: option.value },
        { withCredentials: true }
      );
      
      setPopup({
        show: true,
        title: 'Επιτυχία! 🎉',
        message: `Αγόρασες το ${option.value} κουπόνι! Βρες το στα "Τα Κουπόνια Μου".`,
        type: 'success'
      });
      
      fetchData(); // Refresh
    } catch (error) {
      setPopup({
        show: true,
        title: 'Αποτυχία',
        message: error.response?.data?.detail || 'Δεν ολοκληρώθηκε η αγορά.',
        type: 'error'
      });
    }
  };

  const handleRedeemCoupon = async (couponId) => {
    try {
      const res = await axios.post(`${API}/coupons/${couponId}/redeem`, {}, { withCredentials: true });
      fetchData();
      // Show QR code modal
      setQrModal({ barcode: res.data.barcode, value: res.data.value || '' });
    } catch (error) {
      setPopup({
        show: true,
        title: 'Σφάλμα',
        message: error.response?.data?.detail || 'Δεν μπόρεσε να εξαργυρωθεί.',
        type: 'error'
      });
    }
  };

  const handleShowQr = (coupon) => {
    setQrModal({ barcode: coupon.barcode, value: coupon.value });
  };

  const handleBuySpin = async () => {
    if (coins < 100) {
      setPopup({ show: true, title: 'Ανεπαρκή Coins', message: 'Χρειάζεσαι 50 coins για Extra Spin.', type: 'error' });
      return;
    }
    try {
      await axios.post(`${API}/spins/buy`, {}, { withCredentials: true });
      await fetchData();
      setActiveTab('my-spins'); // Go directly to my spins tab
    } catch (error) {
      setPopup({ show: true, title: 'Σφάλμα', message: error.response?.data?.detail || 'Σφάλμα αγοράς.', type: 'error' });
    }
  };

  const handleShowSpinQr = (spin) => {
    setQrModal({ barcode: spin.barcode, value: 'Extra Spin', isSpin: true });
  };

  const handleEnterContest = async () => {
    if (contestEntry) {
      setPopup({
        show: true,
        title: 'Ήδη Συμμετέχεις',
        message: 'Έχεις ήδη πάρει μέρος στον διαγωνισμό αυτού του μήνα!',
        type: 'info'
      });
      return;
    }

    if (coins < 100) {
      setPopup({
        show: true,
        title: 'Ανεπαρκή Coins',
        message: 'Χρειάζεσαι 100 coins για να συμμετάσχεις στον διαγωνισμό.',
        type: 'error'
      });
      return;
    }

    try {
      await axios.post(`${API}/contest/enter`, {}, { withCredentials: true });
      
      setPopup({
        show: true,
        title: 'Επιτυχία! 🎊',
        message: 'Συμμετέχεις στον διαγωνισμό "Δωρεάν Κούρεμα του Μήνα"! Καλή τύχη!',
        type: 'success'
      });
      
      fetchData(); // Refresh
    } catch (error) {
      setPopup({
        show: true,
        title: 'Αποτυχία',
        message: error.response?.data?.detail || 'Δεν ολοκληρώθηκε η συμμετοχή.',
        type: 'error'
      });
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
        <div className="text-white text-sm">Φόρτωση...</div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black z-50 overflow-y-auto">
        <div className="min-h-screen">
          {/* Header */}
          <div className="bg-black/50 border-b border-white/10">
            <div className="max-w-6xl mx-auto px-4 py-4">
              <div className="flex items-center justify-between">
                {/* Title */}
                <div>
                  <h1 className="text-sm font-light tracking-[0.3em] uppercase" style={{ color: GOLD }}>ELITE</h1>
                  <h2 className="text-white text-lg font-medium leading-tight">Loyalty Reward Shop</h2>
                </div>

                {/* Balance + Refresh (back on the right) */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setHistoryModal(true)}
                    className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded px-3 py-1.5 transition-all cursor-pointer"
                    title="Δες το ιστορικό coins"
                    data-testid="coins-balance-btn"
                  >
                    <span className="text-xl">🪙</span>
                    <span className="text-white text-sm font-medium" data-testid="shop-coins-balance">{coins}</span>
                    <span className="text-gray-500 text-xs">coins</span>
                  </button>
                  <button
                    onClick={fetchData}
                    disabled={refreshing}
                    className="bg-white/5 hover:bg-white/10 border border-white/10 rounded p-1.5 transition-colors disabled:opacity-50"
                    title="Ανανέωση"
                    data-testid="refresh-shop-btn"
                  >
                    <svg 
                      width="16" 
                      height="16" 
                      fill="none" 
                      stroke="white" 
                      strokeWidth="2" 
                      viewBox="0 0 24 24"
                      className={refreshing ? 'animate-spin' : ''}
                    >
                      <path d="M23 4v6h-6M1 20v-6h6"/>
                      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Quick Actions + Main Tabs */}
              <div className="mt-4 space-y-2.5">
                {/* Quick Action Buttons - Compact, one row */}
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setActiveTab('my-coupons')}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all whitespace-nowrap ${
                      activeTab === 'my-coupons' 
                        ? 'bg-white/10 border border-white/20 text-white' 
                        : 'bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid="quick-my-coupons"
                  >
                    <span>🎟️</span>
                    <span>Τα Κουπόνια Μου</span>
                  </button>
                  
                  <button
                    onClick={() => setActiveTab('my-spins')}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all whitespace-nowrap ${
                      activeTab === 'my-spins' 
                        ? 'bg-white/10 border border-white/20 text-white' 
                        : 'bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid="quick-my-spins"
                  >
                    <span>🎡</span>
                    <span>Τα Spins Μου</span>
                  </button>
                  
                  <button
                    onClick={() => setActiveTab('how-it-works')}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all whitespace-nowrap ${
                      activeTab === 'how-it-works' 
                        ? 'bg-white/10 border border-white/20 text-white' 
                        : 'bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid="quick-how-it-works"
                  >
                    <span>ℹ️</span>
                    <span>Πώς Λειτουργεί</span>
                  </button>
                </div>

                {/* Main Tabs - Compact for mobile */}
                <div className="flex items-center gap-1.5 border-t border-white/10 pt-2.5">
                  {[
                    { id: 'coupons', label: 'ΚΟΥΠΟΝΙΑ', emoji: '🎟️' },
                    { id: 'spins', label: 'EXTRA SPINS', emoji: '🎡' },
                    { id: 'contests', label: 'ΔΙΑΓΩΝΙΣΜΟΙ', emoji: '🏆' },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                        activeTab === tab.id 
                          ? 'bg-[#A9B2B9] text-black shadow-lg' 
                          : 'bg-white/5 text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                      data-testid={`tab-${tab.id}`}
                    >
                      <span className="text-sm">{tab.emoji}</span>
                      <span className="hidden sm:inline">{tab.label}</span>
                      <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Compact Content */}
          <div className="max-w-6xl mx-auto px-4 py-6">
            {activeTab === 'coupons' && (
              <CouponsSection coins={coins} onBuy={handleBuyCoupon} />
            )}
            {activeTab === 'spins' && (
              <SpinsSection coins={coins} onBuy={handleBuySpin} />
            )}
            {activeTab === 'my-coupons' && (
              <MyCouponsSection coupons={myCoupons} onRedeem={handleRedeemCoupon} onShowQr={handleShowQr} />
            )}
            {activeTab === 'my-spins' && (
              <MySpinsSection spins={mySpins} />
            )}
            {activeTab === 'contests' && (
              <ContestsSection
                coins={coins}
                entry={contestEntry}
                onEnter={handleEnterContest}
              />
            )}
            {activeTab === 'how-it-works' && (
              <HowItWorksSection />
            )}
          </div>
        </div>
      </div>

      {/* Redemption success notification */}
      {redeemNotif && (
        <div
          className="fixed top-6 left-1/2 -translate-x-1/2 z-[300] flex items-center gap-3 px-5 py-3.5 rounded-lg shadow-2xl border animate-fade-in"
          style={{ background: '#0f0f0f', borderColor: GOLD + '60', minWidth: 280 }}
          data-testid="redeem-success-notif"
        >
          <CheckCircle className="w-5 h-5 shrink-0" style={{ color: GOLD }} />
          <p className="text-white text-sm font-light">{redeemNotif.message}</p>
          <button onClick={() => setRedeemNotif(null)} className="ml-auto text-gray-500 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Coin History Modal */}
      <CoinHistoryModal 
        isOpen={historyModal} 
        onClose={() => setHistoryModal(false)} 
        history={history} 
        currentBalance={coins} 
      />

      {popup.show && (
        <PremiumPopup
          isOpen={popup.show}
          onClose={() => setPopup({ ...popup, show: false })}
          title={popup.title}
          message={popup.message}
          confirmText="OK"
          onConfirm={() => setPopup({ ...popup, show: false })}
          type={popup.type}
        />
      )}

      {/* QR Code Modal */}
      {qrModal && (
        <div 
          className="fixed inset-0 z-[200] flex items-start justify-center bg-black/90 overflow-y-auto pt-8"
          onClick={() => setQrModal(null)}
        >
          <div 
            className="bg-[#111] border border-white/15 rounded-xl p-8 max-w-xs w-full mx-4 text-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setQrModal(null)}
              className="absolute top-3 right-3 text-gray-500 hover:text-white transition-colors"
              data-testid="qr-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            <QrCode className="w-6 h-6 mx-auto mb-3" style={{ color: GOLD }} />
            <h3 className="text-white text-sm font-medium mb-1">Κουπόνι {qrModal.value}</h3>
            <p className="text-gray-400 text-xs mb-5">Δείξε το QR στον κουρέα για σκανάρισμα</p>

            <div className="bg-white rounded-lg p-4 inline-block mb-4">
              <QRCodeSVG
                value={qrModal.barcode}
                size={200}
                level="M"
                includeMargin={false}
              />
            </div>

            <p className="text-gray-600 text-[10px] font-mono break-all">{qrModal.barcode}</p>
          </div>
        </div>
      )}
    </>
  );
}

// Minimal Coupons Section
function CouponsSection({ coins, onBuy }) {
  return (
    <div className="space-y-4">
      {/* Coupon Options */}
      <div className="grid grid-cols-3 gap-3">
        {COUPON_OPTIONS.map((option) => {
          const canAfford = coins >= option.coins;
          return (
            <div
              key={option.coins}
              className={`bg-white/5 border rounded-lg p-4 transition-all ${
                canAfford 
                  ? 'border-white/10 hover:border-[#A9B2B9]/50' 
                  : 'border-white/5 opacity-50'
              }`}
            >
              <div className="text-center">
                <div className="text-white text-2xl font-bold mb-1">{option.value}</div>
                <div className="flex items-center justify-center gap-1 text-[#A9B2B9] text-sm mb-3">
                  <span className="text-base">🪙</span>
                  <span>{option.coins}</span>
                </div>
                
                <button
                  onClick={() => onBuy(option)}
                  disabled={!canAfford}
                  className={`w-full py-2 rounded text-xs font-medium transition-colors ${
                    canAfford
                      ? 'bg-[#A9B2B9] text-black hover:bg-[#d4b55e]'
                      : 'bg-white/5 text-gray-600 cursor-not-allowed'
                  }`}
                >
                  {canAfford ? 'ΑΓΟΡΑ' : 'ΑΝΕΠΑΡΚΗ'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Important Notice - Below coupons */}
      <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl shrink-0">ℹ️</span>
          <div className="space-y-1">
            <p className="text-amber-400 text-sm font-medium">Σημαντική Επισήμανση</p>
            <p className="text-amber-200/80 text-xs leading-relaxed">
              Τα κουπόνια μπορούν να χρησιμοποιηθούν <strong className="text-amber-400">ΜΟΝΟ</strong> για:
            </p>
            <ul className="text-amber-200/80 text-xs space-y-0.5 ml-4 list-disc">
              <li><strong>Προϊόντα</strong> (π.χ. κεριά, πομάδες, κλπ)</li>
              <li><strong>Υπηρεσία Μούσι</strong></li>
            </ul>
            <p className="text-amber-200/80 text-xs mt-2">
              ❌ <strong className="text-amber-400">ΔΕΝ</strong> ισχύουν για κούρεμα
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Minimal My Coupons Section
function MyCouponsSection({ coupons, onRedeem, onShowQr }) {
  if (coupons.length === 0) {
    return (
      <div className="text-center py-12">
        <Ticket className="w-12 h-12 mx-auto mb-3 opacity-20" style={{ color: GOLD }} />
        <p className="text-gray-500 text-sm">Δεν έχεις κουπόνια</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-3">
      {coupons.map((coupon) => (
        <CouponCard key={coupon.coupon_id} coupon={coupon} onRedeem={onRedeem} onShowQr={onShowQr} />
      ))}
    </div>
  );
}

// Minimal Coupon Card
function CouponCard({ coupon, onRedeem, onShowQr }) {
  const isExpired = new Date(coupon.expires_at) < new Date();
  const isUsed = coupon.status === 'used';
  const isRedeemed = coupon.status === 'redeemed';

  return (
    <div className={`bg-white/5 border rounded-lg p-3 ${
      isUsed || isExpired ? 'opacity-40 border-white/5' : 'border-white/10'
    }`}>
      {/* Compact Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="text-[#A9B2B9] text-xl font-bold">{coupon.value}</div>
        <div className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
          isUsed ? 'bg-gray-700 text-gray-400' :
          isExpired ? 'bg-red-900/30 text-red-400' :
          isRedeemed ? 'bg-green-900/30 text-green-400' :
          'bg-[#A9B2B9]/20 text-[#A9B2B9]'
        }`}>
          {isUsed ? 'USED' : isExpired ? 'EXPIRED' : isRedeemed ? 'READY' : 'ACTIVE'}
        </div>
      </div>

      {/* Expiry */}
      <div className="text-gray-500 text-[10px] mb-3">
        Λήγει: {new Date(coupon.expires_at).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' })}
      </div>

      {/* Action */}
      {!isUsed && !isExpired && !isRedeemed && (
        <button
          onClick={() => onRedeem(coupon.coupon_id)}
          className="w-full py-2 bg-[#A9B2B9] text-black rounded text-xs font-medium hover:bg-[#d4b55e] transition-colors"
          data-testid={`redeem-coupon-${coupon.coupon_id}`}
        >
          ΕΞΑΡΓΥΡΩΣΗ
        </button>
      )}

      {/* Show QR button for redeemed coupons */}
      {isRedeemed && coupon.barcode && (
        <button
          onClick={() => onShowQr(coupon)}
          className="w-full py-2 bg-white/10 border border-white/20 text-white rounded text-xs font-medium hover:bg-white/15 transition-colors flex items-center justify-center gap-1.5"
          data-testid={`show-qr-${coupon.coupon_id}`}
        >
          <QrCode className="w-3.5 h-3.5" />
          ΕΜΦΑΝΙΣΗ QR
        </button>
      )}

      {isUsed && (
        <div className="text-center text-gray-500 text-[10px] py-1">Χρησιμοποιήθηκε</div>
      )}
    </div>
  );
}

// Minimal Contests Section
function ContestsSection({ coins, entry, onEnter }) {
  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="bg-[#A9B2B9]/10 rounded p-2">
            <Trophy className="w-6 h-6" style={{ color: GOLD }} />
          </div>
          
          <div className="flex-1">
            <h3 className="text-white text-lg font-bold mb-1">
              Δωρεάν Κούρεμα του Μήνα
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Συμμέτεχε με 100 coins στην κλήρωση
            </p>

            {entry ? (
              <div className="bg-green-900/20 border border-green-700/30 rounded px-3 py-2 text-green-400 text-sm">
                ✓ Συμμετέχεις στον διαγωνισμό
              </div>
            ) : (
              <button
                onClick={onEnter}
                disabled={coins < 100}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                  coins >= 100
                    ? 'bg-[#A9B2B9] text-black hover:bg-[#d4b55e]'
                    : 'bg-white/5 text-gray-600 cursor-not-allowed'
                }`}
              >
                {coins >= 100 ? 'ΣΥΜΜΕΤΟΧΗ (100 coins)' : 'ΑΝΕΠΑΡΚΗ COINS'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// How It Works Section
function HowItWorksSection() {
  const steps = [
    { icon: '✂', title: 'Κούρεμα', desc: 'Κάθε επίσκεψη στο barbershop σου χαρίζει 50 coins αυτόματα.' },
    { icon: '🎡', title: 'Spin the Wheel', desc: 'Μετά από κάθε κούρεμα ενεργοποιείται ο τροχός. Κέρδισε επιπλέον coins (50, 100 ή 200) ή άλλες εκπλήξεις.' },
    { icon: '🪙', title: 'Μάζεψε Coins', desc: 'Κάθε κούρεμα → 50 coins\nΚέρδισε από τον Τροχό της Τύχης\n\n⏰ Τα coins λήγουν μετά από 1 χρόνο από την ημερομηνία που τα κέρδισες.' },
    { icon: '🎟', title: 'Αγόρασε Κουπόνια', desc: '300 coins → 3€ κουπόνι\n500 coins → 5€ κουπόνι\n1000 coins → 10€ κουπόνι\n\n⚠️ Ισχύουν ΜΟΝΟ για προϊόντα ή μούσι (ΟΧΙ κούρεμα)' },
    { icon: '🎫', title: 'Εξαργύρωση Κουπονιού', desc: 'Πάτα "ΕΞΑΡΓΥΡΩΣΗ" για να εμφανιστεί QR code. Δείξε το στον barber να το σκανάρει και απόλαυσε το κουπόνι.' },
    { icon: '🎡', title: 'Extra Spin', desc: '50 coins → αγοράζεις 1 επιπλέον στροφή τροχού που ενεργοποιείται αμέσως και έχει ισχύ 7 μέρες. Πήγαινε στα "Τα Spins Μου" για να το χρησιμοποιήσεις.' },
    { icon: '🏆', title: 'Μηνιαίος Διαγωνισμός', desc: 'Μπες στην κλήρωση με 100 coins. Ο νικητής ανακοινώνεται την τελευταία μέρα κάθε μήνα.' },
  ];

  return (
    <div className="max-w-lg mx-auto space-y-3" data-testid="how-it-works">
      <div className="text-center mb-6">
        <p className="text-xs tracking-[0.3em] uppercase mb-1" style={{ color: GOLD }}>Οδηγός</p>
        <h2 className="text-white text-base font-light">Πώς Λειτουργεί το Elite Loyalty Reward Shop</h2>
      </div>

      {steps.map((step, i) => (
        <div key={i} className="flex gap-4 p-4 border border-white/8 rounded-lg bg-white/3 hover:border-white/15 transition-colors">
          <div className="text-xl shrink-0 w-8 text-center">{step.icon}</div>
          <div className="min-w-0">
            <p className="text-white text-xs font-medium mb-1">{step.title}</p>
            <p className="text-gray-500 text-xs font-light leading-relaxed whitespace-pre-line">{step.desc}</p>
          </div>
        </div>
      ))}

      <div className="mt-6 p-4 rounded-lg border text-center" style={{ borderColor: `${GOLD}30`, background: `${GOLD}08` }}>
        <p className="text-xs font-light" style={{ color: GOLD }}>Κάθε κούρεμα = 50 coins</p>
        <p className="text-gray-500 text-xs mt-1">Μάζεψε 300+ coins και αρχίζεις να κερδίζεις εκπτώσεις!</p>
      </div>
    </div>
  );
}

// Extra Spins Purchase Section
function SpinsSection({ coins, onBuy }) {
  return (
    <div className="max-w-sm mx-auto">
      <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center space-y-4">
        <RotateCw className="w-10 h-10 mx-auto" style={{ color: GOLD }} />
        <div>
          <h3 className="text-white text-sm font-medium">Extra Spin</h3>
          <p className="text-gray-500 text-xs mt-1">Ξαναγύρισε τον τροχό ακόμη και αν δεν έχεις κλείσει ραντεβού</p>
        </div>
        <div className="border border-white/10 rounded-lg p-3 bg-white/3">
          <p className="text-gray-400 text-xs">Κόστος</p>
          <p className="text-2xl font-light" style={{ color: GOLD }}>50</p>
          <p className="text-gray-500 text-xs">coins</p>
        </div>
        <p className="text-gray-600 text-xs">Υπόλοιπό σου: <span style={{ color: coins >= 50 ? GOLD : '#888' }}>{coins} coins</span></p>
        <button
          onClick={onBuy}
          disabled={coins < 50}
          className="w-full py-2.5 text-xs font-medium tracking-widest uppercase rounded transition-all disabled:opacity-40"
          style={{ background: coins >= 50 ? GOLD : '#333', color: coins >= 50 ? '#000' : '#666' }}
          data-testid="buy-extra-spin-btn"
        >
          ΑΓΟΡΑ EXTRA SPIN
        </button>
      </div>
    </div>
  );
}

// My Spins Section
function MySpinsSection({ spins }) {
  if (spins.length === 0) {
    return (
      <div className="text-center py-12">
        <RotateCw className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: GOLD }} />
        <p className="text-gray-500 text-sm">Δεν έχεις Extra Spins</p>
        <p className="text-gray-600 text-xs mt-1">Αγόρασε ένα από το tab "EXTRA SPIN"</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-w-lg mx-auto">
      {spins.map(spin => (
        <SpinCard key={spin.spin_id} spin={spin} />
      ))}
    </div>
  );
}

function SpinCard({ spin }) {
  const isActive = spin.status === 'active';
  const isUsed = spin.status === 'used';
  const isExpired = spin.status === 'expired';

  const statusLabel = isActive ? 'ΕΝΕΡΓΟ' : isUsed ? 'ΧΡΗΣΙΜΟΠΟΙΗΘΗΚΕ' : 'ΛΗΞΕ';
  const statusColor = isActive ? '#4ade80' : '#555';

  const expiresAt = spin.expires_at ? new Date(spin.expires_at) : null;
  const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - new Date()) / 86400000)) : null;

  return (
    <div className={`border rounded-xl p-4 flex items-center gap-4 ${!isActive ? 'opacity-40 border-white/5' : 'border-white/15'}`}
      style={{ background: isActive ? `${GOLD}06` : 'rgba(255,255,255,0.02)' }}>
      <RotateCw className={`w-8 h-8 shrink-0 ${isActive ? 'animate-spin-slow' : ''}`} style={{ color: isActive ? GOLD : '#444' }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-white text-xs font-medium">Extra Spin</span>
          <span className="text-[10px] px-2 py-0.5 rounded font-bold" style={{ color: statusColor, background: `${statusColor}20` }}>
            {statusLabel}
          </span>
        </div>
        {isActive && expiresAt && (
          <p className="text-xs" style={{ color: daysLeft <= 2 ? '#f87171' : '#888' }}>
            Λήγει σε {daysLeft} {daysLeft === 1 ? 'μέρα' : 'μέρες'} · {expiresAt.toLocaleDateString('el-GR')}
          </p>
        )}
        {isActive && (
          <p className="text-xs mt-1" style={{ color: '#4ade80' }}>
            Πήγαινε στον τροχό και γύρισε τον!
          </p>
        )}
        {(isUsed || isExpired) && (
          <p className="text-gray-600 text-[10px]">{new Date(spin.created_at).toLocaleDateString('el-GR')}</p>
        )}
      </div>
    </div>
  );
}

// Coin History Modal Component
function CoinHistoryModal({ isOpen, onClose, history, currentBalance }) {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80"
      onClick={onClose}
    >
      <div 
        className="bg-black border border-white/20 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h3 className="text-white text-lg font-medium">Ιστορικό Coins</h3>
            <p className="text-gray-400 text-sm mt-1">Όλες οι συναλλαγές σου</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* History List */}
        <div className="overflow-y-auto max-h-[60vh] p-6">
          {history.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🪙</div>
              <p className="text-gray-500">Δεν υπάρχει ιστορικό ακόμα</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item, idx) => {
                const isEarned = item.action === 'earned';
                const amount = item.balance ?? item.points ?? 0;
                const date = new Date(item.date);
                const isExpired = item.expires_at && new Date(item.expires_at) < new Date();
                
                return (
                  <div
                    key={item.id || idx}
                    className="flex items-start gap-4 p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    {/* Icon */}
                    <div 
                      className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                        isEarned ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}
                    >
                      {isEarned ? (
                        <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">
                            {item.description || (isEarned ? 'Κέρδισες coins' : 'Εξαργύρωση')}
                          </p>
                          <p className="text-gray-500 text-xs mt-0.5">
                            {date.toLocaleDateString('el-GR', { 
                              day: '2-digit', 
                              month: 'short', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          {isEarned && item.expires_at && (
                            <p className={`text-xs mt-1 ${isExpired ? 'text-red-400' : 'text-gray-600'}`}>
                              {isExpired ? '⚠️ Έληξαν' : `Λήγουν: ${new Date(item.expires_at).toLocaleDateString('el-GR')}`}
                            </p>
                          )}
                        </div>

                        {/* Amount */}
                        <div className="text-right shrink-0">
                          <p className={`text-sm font-bold ${isEarned ? 'text-green-400' : 'text-red-400'}`}>
                            {isEarned ? '+' : '-'}{amount}
                          </p>
                          <p className="text-gray-600 text-xs">coins</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 bg-white/5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs">Τρέχον Balance</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-2xl">🪙</span>
                <span className="text-white text-xl font-bold">{currentBalance}</span>
                <span className="text-gray-500 text-sm">coins</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
            >
              Κλείσιμο
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RewardShop;
